import type { Dayjs } from 'dayjs'
import { z } from 'zod'
import type {
  DonationBox,
  DonationBoxId,
  WebsiteDonationBox
} from '#shared/types/donations'
import type { AbstractId } from '#shared/types/utils'
import type { UserId } from '#shared/types/users'

export type RecurringDonationId = AbstractId<'recurring-donation'>

export enum RecurringDonationPeriodicTypeEnum {
  OneTime = 'one-time',
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  Yearly = 'yearly'
}

export const RecurringDonationPeriodicTypeOptions = z.enum(
  RecurringDonationPeriodicTypeEnum
)
export type RecurringDonationPeriodicType = z.infer<
  typeof RecurringDonationPeriodicTypeOptions
>

export enum RecurringDonationStatusEnum {
  NoPaymentInformation = 'no-payment-information',
  Active = 'active',
  Cancelled = 'cancelled'
}

export const RecurringDonationStatusOptions = z.enum(RecurringDonationStatusEnum)
export type RecurringDonationStatus = z.infer<typeof RecurringDonationStatusOptions>

export interface AddDonationParams {
  amount: number

  periodicType: RecurringDonationPeriodicType

  slug?: string
  userId: UserId | null
  donationBoxId: DonationBoxId | null

  isCorporate: boolean

  donor: {
    firstname: string
    lastname: string
    email: string
  }
}

export interface DonationDonorInformation {
  firstname: string | null
  lastname: string | null
  email: string | null
  phone: string | null
  city: string | null
}

export interface RecurringDonation {
  id: RecurringDonationId
  createdAt: Dayjs

  userId: UserId | null

  identifier: string
  amount: number
  periodicType: RecurringDonationPeriodicType
  status: RecurringDonationStatus

  consentAt: Dayjs | null
  recurringToken: string | null

  nextDonationDate: Dayjs | null

  comments: string | null
  slug: string | null

  hasGivenContactConsent: boolean
  isAnonymous: boolean

  donorInformation: DonationDonorInformation
}

export interface WebsiteRecurringDonation {
  id: RecurringDonationId
  userId: UserId
  createdAt: Dayjs

  identifier: string

  amount: number
  periodicType: RecurringDonationPeriodicType
  status: RecurringDonationStatus

  nextDonationDate: Dayjs | null

  hasPaymentInfo: boolean

  comments: string | null
}

export type CreateDonationBoxSteps = 'register' | 'form' | 'preview' | 'finish'

export interface GetDonationBoxResult {
  donationBox: DonationBox
  donationStatistics: WebsiteDonationBox
}

export interface CancelDonationParams {
  fullName: string
  email: string
  donationIdentifier: string | null
  comments: string | null
}
